import {
  get,
  isEmpty,
  snakeCase,
  omit,
  pick,
  upperFirst,
  startCase,
  camelCase,
  upperCase,
} from 'lodash';
import {
  format,
  differenceInHours,
  differenceInMinutes,
  compareDesc,
  isValid,
  addDays,
  isDate,
} from 'date-fns';

const formatDate = (date, pattern = 'YYYY-MM-DD', options = false) => {
  const d = new Date(date);
  if (typeof options === 'object' && options !== null) {
    return format(d, pattern, options);
  }
  return format(d, pattern);
};

const formatTimestamp = date => formatDate(date, 'YYYY-MM-DD HH:mm:ss');
const formatDateOnly = date => formatDate(date, 'YYYY-MM-DD');
const formatDateReadable = (date, hasDay) => formatDate(date, hasDay ? 'MMMM D YYYY, dddd' : 'MMMM D YYYY');
const formatTimeOnly = (date, is24H = false, withSeconds = false) => {
  let pattern = is24H ? 'HH:mm' : 'hh:mm';
  if (withSeconds) {
    pattern = `${pattern}:ss`;
  }
  if (!is24H) {
    pattern = `${pattern} A`;
  }
  return formatDate(date, pattern);
};

const toString = (value) => {
  if (value === null || typeof value === 'undefined') {
    return '';
  }
  if (value instanceof Object) {
    return Object.keys(value)
      .sort()
      .map(key => toString(value[key]))
      .join(' ');
  }
  return String(value);
};

const roundNum = (num, precision) => {
  const multiplier = ((10 ** precision) || 0);
  return Math.round(num * multiplier) / multiplier;
};

const scrollToTop = () => {
  window.scrollTo(0, 0);
};

const swapArr = (arr) => {
  if (!Array.isArray(arr)) {
    return arr;
  }

  const [a, b] = arr;

  return [b, a];
};

const timeStrToDateObj = (timeStr) => {
  if (!timeStr) {
    return null;
  }
  const tArr = timeStr.split(':');
  const [hh, mm] = tArr;

  return new Date(1970, 1, 1, hh, mm);
};

const dateCompare = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  if (isValid(d1) && isValid(d2)) {
    return compareDesc(d1, d2);
  }
  return null;
};

const cloneDateSetTime = (date, hh, mm, ss) => {
  const d = new Date(date);
  if (hh || hh === 0) {
    d.setHours(hh);
  }
  if (mm || mm === 0) {
    d.setMinutes(mm);
  }
  if (ss || ss === 0) {
    d.setSeconds(ss);
  }
  return d;
};

const formatForCSV = (data = [], filename) => {
  if (!Array.isArray(data)) {
    // eslint-disable-next-line
    console.error('Data invalid');
    return;
  }
  const csvData = data.map(row => row.join(',')).join('\n');

  const blob = new Blob([csvData], { type: 'text/csv' });
  // generate new link
  const url = window.URL.createObjectURL(blob);

  const fileLink = document.getElementById('file_link');

  fileLink.setAttribute('href', url);
  fileLink.setAttribute('download', filename || `file-${Date.now()}.csv`);

  fileLink.click();

  // reset file link
  fileLink.setAttribute('href', '');
};

const modifiedIsEmpty = (x) => {
  switch (typeof x) {
    case 'object':
      if (isDate(x)) {
        return !x;
      }
      return isEmpty(x);
    case 'boolean':
    case 'number':
      return !x;
    case 'string':
      return x.length === 0;
    default:
      return isEmpty(x);
  }
};

const modifyObjectPropsCasing = (obj, caseToUse = 'snakeCase') => {
  const keys = Object.keys(obj);
  const newObj = {};
  const cases = {
    snakeCase,
    startCase,
    camelCase,
  };
  keys.forEach((k) => {
    newObj[cases[caseToUse](k)] = obj[k];
  });

  return newObj;
};

const funcObject = {
  get,
  omit,
  pick,
  startCase,
  addDays,
  isDate,
  mopc: modifyObjectPropsCasing,
  fcsv: formatForCSV,
  cdst: cloneDateSetTime,
  dateComp: dateCompare,
  tsd: timeStrToDateObj,
  camel: camelCase,
  ie: modifiedIsEmpty,
  snake: snakeCase,
  k: Object.keys,
  fd: formatDate,
  fdo: formatDateOnly,
  fdr: formatDateReadable,
  fto: formatTimeOnly,
  fts: formatTimestamp,
  diffH: differenceInHours,
  diffMin: differenceInMinutes,
  swap: swapArr,
  st: toString,
  round: roundNum,
  title: upperFirst,
  upper: upperCase,
  backToTop: scrollToTop,
};
const funcKeys = Object.keys(funcObject);
const funcList = funcKeys.map(fn => ({
  name: fn,
  fn: funcObject[fn],
}));

export default funcList;
